import React, { useCallback, useId, useLayoutEffect, useRef } from "react";
import BdrSelectPopup from "./BdrSelectPopup";
import {
  autoCapSentences as applyAutoCapSentences,
  AUTO_CAP_INITIAL_STATE,
} from "../../utils/formatters/autoCapitalize";

/**
 * BdrInput — the app's own text input, textarea and select.
 *
 * Deliberately NOT a wrapper around MUI's TextField. `CssTextFieldStandard`
 * exists and is used in ~40 files, but styling it means fighting MUI's
 * internals through `& .MuiInputBase-input` selectors, and its `helperText`
 * is hard-styled as an amber error pill — so a plain descriptive hint
 * renders as a warning badge. Owning the markup is the point: the chrome is
 * four CSS classes we control.
 *
 * The visual treatment matches the calendar event modal's dropdowns, which
 * is the look this app already reads as "a field".
 *
 * Renders the full field stack in a fixed order:
 *
 *   label
 *   error      ← validation, as a danger banner, directly under the label
 *   control    ← input | textarea | select
 *   helper     ← descriptive text: what this field means
 *   warning    ← a caution banner, below the helper
 *
 * error and warning are independent — a field can show both (e.g. "required"
 * plus "changing this affects past sends").
 */
const BdrInput = ({
  label,
  value,
  onChange,
  name,
  id,
  type = "text",
  placeholder,
  helper,
  error,
  warning,
  /** render a <textarea> */
  multiline = false,
  rows = 3,
  /**
   * Multiline only. ON by default: the textarea grows to fit its content
   * instead of scrolling inside itself, so the surrounding panel does the
   * scrolling and `rows` is just the MINIMUM height.
   *
   * The default is on because the alternative kept producing the same bug. A
   * fixed-height textarea inside a scrollable modal gives two nested
   * scrollbars, and the inner one is the wrong one to reach for — it hides
   * text that had room to be shown. That was reported twice (the task note,
   * then the client note) before this became the default.
   *
   * Pass `autoGrow={false}` where a fixed box is genuinely wanted — a field
   * whose container does NOT scroll, where unbounded growth would push the
   * surrounding controls off screen.
   */
  autoGrow = true,
  /** render a <select>; array of { value, label } or plain strings */
  options,
  /**
   * With `options`: render the list as a real DOM popup instead of a native
   * <select>. ON by default since 2026-09-23.
   *
   * It started opt-in, for lists long enough to run off the screen. That was
   * the wrong default for this app. A native list is OS chrome: CSS reaches
   * its background and text colour and nothing else, so every native dropdown
   * opened as a plain system list hanging off a control that had just been
   * carefully styled — and with no height cap, a long one ran past the edge of
   * the browser window. Both are the same failure: the app stops looking like
   * one app the moment a list opens.
   *
   * `popup={false}` is the escape hatch. The honest cost of the default is
   * mobile: a native <select> gets the OS picker, which is a better target for
   * a thumb than a floating list. Use it where a control is genuinely
   * phone-first.
   */
  popup = true,
  /** With `popup`: size the list to its content rather than to the field.
      Only needed for a narrow field — see BdrSelectPopup. */
  fitContent = false,
  /** "md" (default) | "sm" — sm pairs with BdrButton size="sm" so a field
      sitting beside a button is the same height. */
  size = "md",
  /**
   * Node pinned inside the control's right edge — a search spinner, a unit
   * suffix, a clear button. Replaces MUI's `InputProps.endAdornment`, which
   * is the only reason a couple of search boxes could not move off TextField.
   *
   * The control gets extra right padding so text never runs under it. Not for
   * a <select>: the chevron already occupies that space.
   */
  trailing,
  /**
   * Show a spinner inside the control's right edge — for a search box waiting
   * on a debounced query.
   *
   * A first-class prop rather than something each caller builds, because three
   * search boxes were each hand-rolling one and they had drifted: two inherited
   * the field's white, one set `#ffffff` directly, and a fourth had it wired
   * through MUI's `InputProps.endAdornment`, which `BdrInput` does not read —
   * so that one silently showed nothing at all.
   *
   * It reserves the trailing slot whether or not it is spinning. That is the
   * same wrapper-stability rule as `trailing` itself: a slot that appears and
   * disappears remounts the input and loses focus mid-keystroke.
   *
   * NO DEFAULT on purpose. `loading = false` would be *defined* for every
   * caller, so every field in the app would reserve trailing space it never
   * uses. Presence means "this field has a spinner"; truthiness means "it is
   * spinning right now".
   */
  loading,
  /**
   * Text / textarea only. Capitalise the first letter of the message and of
   * each sentence as the user TYPES, the way a phone keyboard does — with the
   * phone's refusal (delete our capital, retype the letter, it stays
   * lowercase). URLs, e-mail addresses, decimals, abbreviations and ellipses
   * are left alone, and a deletion never capitalises anything. The rules
   * live in `utils/formatters/autoCapitalize.js`; this component only owns
   * the state between keystrokes.
   *
   * Not named `autoCapitalize` because that is the DOM attribute, which is a
   * hint to a VIRTUAL keyboard and does nothing on a physical one — the
   * reason this exists. While this is on, the DOM hint is set to "off" so
   * one system owns the field on every device (pass `autoCapitalize`
   * yourself to override that).
   *
   * The caller still receives a normal change event; `event.target.value` is
   * the corrected text. OFF by default: no existing field changes behaviour.
   */
  autoCapSentences = false,
  disabled = false,
  required = false,
  className = "",
  inputClassName = "",
  inputRef,
  ...rest
}) => {
  // Stable generated id so the <label> actually points at the control when
  // the caller does not supply one. A label with no `for` is decorative:
  // clicking it does nothing and a screen reader never associates the two.
  const autoId = useId();
  const controlId = id || `${name || "field"}-${autoId}`;

  // The control node, kept locally for autoGrow AND handed to the caller's
  // `inputRef` — several callers need it (setSelectionRange, scrollIntoView,
  // a popper anchor), so this cannot simply take the ref over.
  // Whether the caller uses the trailing slot at all — a prop that is present
  // but currently false still reserves the space, so neither the markup nor
  // the padding shifts when its content appears.
  const hasTrailingSlot = trailing !== undefined || loading !== undefined;

  const trailingContent = loading ? (
    <span className="bdr-input-spinner" role="status" aria-label="Searching" />
  ) : (
    trailing
  );

  const controlRef = useRef(null);
  const setControlRef = useCallback(
    (node) => {
      controlRef.current = node;
      if (typeof inputRef === "function") inputRef(node);
      else if (inputRef) inputRef.current = node;
    },
    [inputRef]
  );

  // Measure AFTER layout but BEFORE paint, or the box visibly jumps from its
  // `rows` height to the fitted one on every open.
  useLayoutEffect(() => {
    if (!autoGrow || !multiline) return;
    const el = controlRef.current;
    if (!el) return;
    // Reset first: scrollHeight can only grow while a height is pinned, so
    // without this the field never shrinks back after text is deleted.
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [autoGrow, multiline, value]);

  const describedBy =
    [
      helper ? `${controlId}-helper` : null,
      error ? `${controlId}-error` : null,
      warning ? `${controlId}-warning` : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  const controlClass = [
    "bdr-input",
    size === "sm" ? "bdr-input--sm" : "",
    error ? "bdr-input--invalid" : "",
    /* the slot, not its contents — so the text does not shift sideways when
       a spinner appears and disappears */
    hasTrailingSlot ? "bdr-input--has-trailing" : "",
    autoGrow && multiline ? "bdr-input--grow" : "",
    inputClassName,
  ]
    .filter(Boolean)
    .join(" ");

  // Auto-capitalisation state between keystrokes. Refs, not state: nothing
  // renders from it, and it must be current inside the very next event.
  const capOn = autoCapSentences && !options;
  const autoCapStateRef = useRef(AUTO_CAP_INITIAL_STATE);
  const autoCapLastValueRef = useRef(value ?? "");

  const handleAutoCapChange = (event) => {
    const el = event.target;
    // Mid-composition (an IME assembling a CJK syllable) the DOM text is not
    // a keystroke yet; rewriting it would break the composition.
    if (!event.nativeEvent?.isComposing) {
      const prev = value ?? "";
      // The value was replaced from outside (a send cleared it, a retry
      // refilled it), so the state describes text that is gone. Start over.
      if (prev !== autoCapLastValueRef.current) {
        autoCapStateRef.current = AUTO_CAP_INITIAL_STATE;
      }
      const raw = el.value ?? "";
      const result = applyAutoCapSentences(prev, raw, autoCapStateRef.current);
      autoCapStateRef.current = result.state;
      autoCapLastValueRef.current = result.value;
      if (result.value !== raw) {
        // Rewrite the control in place: the caller reads the corrected text
        // from `event.target.value`, and React's controlled restore then finds
        // the DOM already equal to the state it is handed, so it does not
        // assign again. Assigning `.value` parks the caret at the end, so put
        // it back — the rewrite never changes length, so the old offsets are
        // still right (this matters when editing mid-message).
        const { selectionStart, selectionEnd, selectionDirection } = el;
        el.value = result.value;
        try {
          el.setSelectionRange(selectionStart, selectionEnd, selectionDirection);
        } catch {
          /* input types with no selection API (email, number) — nothing to restore */
        }
      }
    }
    if (onChange) onChange(event);
  };

  const shared = {
    id: controlId,
    name,
    value,
    onChange: capOn ? handleAutoCapChange : onChange,
    disabled,
    required,
    className: controlClass,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy,
    autoCapitalize: capOn ? "off" : undefined,
    ...rest,
  };

  let control;
  if (options && popup) {
    control = (
      <BdrSelectPopup
        options={options}
        value={value}
        onChange={onChange}
        name={name}
        id={controlId}
        placeholder={placeholder}
        disabled={disabled}
        className={`${controlClass} bdr-select-trigger`}
        describedBy={describedBy}
        invalid={!!error}
        fitContent={fitContent}
        /* titles the sheet on a narrow screen */
        label={label}
      />
    );
  } else if (options) {
    control = (
      <select ref={setControlRef} {...shared}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => {
          const val = typeof opt === "string" ? opt : opt.value;
          const text = typeof opt === "string" ? opt : opt.label;
          return (
            <option key={val} value={val}>
              {text}
            </option>
          );
        })}
      </select>
    );
  } else if (multiline) {
    control = (
      <textarea
        ref={setControlRef}
        rows={rows}
        placeholder={placeholder}
        {...shared}
      />
    );
  } else {
    control = (
      <input ref={setControlRef} type={type} placeholder={placeholder} {...shared} />
    );
  }

  return (
    <div className={`bdr-field ${className}`.trim()}>
      {label && (
        <label className="bdr-field-label" htmlFor={controlId}>
          {label}
          {required && <span aria-hidden="true"> *</span>}
        </label>
      )}

      {error && (
        <p
          id={`${controlId}-error`}
          className="bdr-alert bdr-alert--danger bdr-alert--inline"
          role="alert"
        >
          {error}
        </p>
      )}

      {/* The wrapper only appears when there IS something trailing, so a
          plain field keeps the flatter markup. */}
      {/*
        The wrapper's presence tracks whether the caller USES this slot, not
        whether it currently has something in it.

        Those are different, and the difference is a focus bug. Callers write
        `trailing={isLoading && <Spinner/>}`, so the value flips between false
        and an element as state changes. Keying the wrapper on truthiness put
        a different element type at this position in the tree, so React
        unmounted the <input> and mounted a fresh one — and the field lost
        focus mid-keystroke. The client search box did this on EVERY letter,
        because its spinner state toggles on every change.
      */}
      {hasTrailingSlot ? (
        <div className="bdr-input-wrap">
          {control}
          {trailingContent ? (
            <span className="bdr-input-trailing">{trailingContent}</span>
          ) : null}
        </div>
      ) : (
        control
      )}

      {helper && (
        <span id={`${controlId}-helper`} className="bdr-field-helper">
          {helper}
        </span>
      )}

      {warning && (
        <p
          id={`${controlId}-warning`}
          className="bdr-alert bdr-alert--warning bdr-alert--inline"
        >
          {warning}
        </p>
      )}
    </div>
  );
};

export default BdrInput;
